/* global JSX */
import * as React from "react";
import * as userService from "../../models/services/userService";
import * as tokenService from "../../models/services/tokenService";
import * as appService from "../../models/services/appService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolAddFileButton, ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { customHistory } from "../../framework/customHistory";
import { Session } from "../../models/session/session";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { IUserState } from "../../models/store/storeTypes";
import { UserProfileDialog } from "../user/userProfileDialog";
import { AppUtils } from "../../framework/appUtils";
import { IUserProfileEdit } from "../../models/user/userProfileEdit";
import { ISettingsEdit } from "../../models/settings/settingsEdit";
import { SettingsDialog } from "../settings/settingsDialog";
import { IIdCodeNameLogo } from "source/models/common/idCodeNameLogo";

export interface IHeaderMenuProp {
    canEditOwnRecord: boolean;
    canUseCalendarTasks: boolean;
    onShowUserProfile: () => void;
    onShowSettings: () => void;
    onLogout: () => void;
    onHideMenu: () => void;
}

export class HeaderMenu extends React.Component<IHeaderMenuProp, {}> {
    preventDefaultAndStopPropagation = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    handleUserProfileClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        this.props.onShowUserProfile();
    };

    handleCalendarClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        customHistory.push("/calendartask");
        this.props.onHideMenu();
    };

    handleFurtherEducationsClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        customHistory.push("/userfurthereducation");
        this.props.onHideMenu();
    };

    handleIdCardClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        customHistory.push("/useridcard");
        this.props.onHideMenu();
    };

    handleSettingsClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        this.props.onShowSettings();
    };
    
    handleLogOutClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        if (!this.props.onLogout) return;
        this.props.onLogout();
    };

    handleBackgroundClick = (e) => {
        this.preventDefaultAndStopPropagation(e);
        this.props.onHideMenu();
    };
    
    render() {
        const username = Session.getAuthenticatedUserNameLocalStorage();
        return (
            <div className="headerMenu" onClick={this.handleBackgroundClick}>
                <div className="menu">
                    <div className="line" onClick={this.handleUserProfileClick}>
                        <span className="icon user mui"></span><span className="text">{username}</span>
                    </div>
                    {this.props.canUseCalendarTasks &&
                        <div className="line" onClick={this.handleCalendarClick}>
                            <span className="icon calendar mui"></span><span className="text">{Translations.Calendar}</span>
                        </div>
                    }
                    {this.props.canEditOwnRecord &&
                        <div className="line" onClick={this.handleFurtherEducationsClick}>
                            <span className="icon furtherEducation"></span><span className="text">{Translations.FurtherEducations}</span>
                        </div>
                    }
                    {this.props.canEditOwnRecord &&
                        <div className="line" onClick={this.handleIdCardClick}>
                            <span className="icon idCard mui"></span><span className="text">{Translations.IdCard}</span>
                        </div>
                    }
                    <div className="line" onClick={this.handleSettingsClick}>
                        <span className="icon settings mui"></span><span className="text">{Translations.Settings}</span>
                    </div>
                    <div className="line" onClick={this.handleBackgroundClick}>&nbsp;</div>
                    <div className="line" onClick={this.handleLogOutClick}>
                        <span className="icon logout mui"></span><span className="text">{Translations.LogOut}</span>
                    </div>
                </div>
            </div>
        );
    }
}

export interface IHeaderCompanyMenuProp {
    companyId: string;
    companies: IIdCodeNameLogo[];
    onChange: (companyId: string) => void;
    onHideMenu: () => void;
}

export const HeaderCompanyMenu = (props: IHeaderCompanyMenuProp) => {
    const preventDefaultAndStopPropagation = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleClick = (e, id: string) => {
        preventDefaultAndStopPropagation(e);
        if (!id) return;
        const company = props.companies.find(i => i.id === id);
        if (!company) return;
        props.onChange(company.id);
    };

    const handleBackgroundClick = (e) => {
        preventDefaultAndStopPropagation(e);
        props.onHideMenu();
    };
    
    return (
        <div className="headerMenu" onClick={handleBackgroundClick}>
            <div className="menu" style={{ marginLeft: "60px" }}>
                {props.companies.map(company => (
                    <div key={company.id} className={"line" + (props.companyId === company.id ? " active" : "")} onClick={(e) => handleClick(e, company.id)}>
                        <div className="row">
                            <div className="col col-12">
                                {!!company.logo &&
                                    <div className="iconCont">
                                        <img alt="..." src={company.logo} />
                                    </div>
                                }
                                {!!company.logo &&
                                    <div className="dataCont">
                                        <div className="singleLine">{company.name}</div>
                                    </div>
                                }
                                {!company.logo &&
                                    <div className="singleLine">{company.name}</div>
                                }
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export interface IHeaderProp {
    user: IUserState;
    onLogout: () => void;
}

export interface IHeaderState {
    showMenu: boolean;
    showUserProfile: boolean;
    userProfileEdit: IUserProfileEdit;
    showSettings: boolean;
    settingsEdit: ISettingsEdit;
    showCompanies: boolean;
    companies: IIdCodeNameLogo[];
}

export class Header extends React.Component<IHeaderProp, IHeaderState> {
    constructor(props) {
        super(props);
        this.state = {
            showMenu: false,
            showUserProfile: false,
            userProfileEdit: null,
            showSettings: false,
            settingsEdit: null,
            showCompanies: false,
            companies: []
        };
    }
    
    handleMenuClick = () => {
        this.setState({ showMenu: !this.state.showMenu });
    };
      
    handleHideMenu = () => {
        this.setState({ showMenu: false });
    };

    handleLogoClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        customHistory.push("/");
    };

    // #region UserProfile
    handleShowUserProfile = async () => {
        const userProfileEdit = await AppUtils.callService(() => userService.getUserProfileEdit());
        if (!userProfileEdit) return null;
        this.setState({
            showUserProfile: true,
            userProfileEdit
        });
        this.handleHideMenu();
    };

    handleUserProfileDialogClose = () => {
        this.setState({
            showUserProfile: false
        });
    };
    // #endregion UserProfile

    // #region Settings
    handleShowSettings = async () => {
        const settingsEdit = await AppUtils.callService(() => appService.getSettingsEdit());
        if (!settingsEdit) return null;
        this.setState({
            showSettings: true,
            settingsEdit
        });
        this.handleHideMenu();
    };

    handleSettingsDialogClose = () => {
        this.setState({
            showSettings: false
        });
    };
    // #endregion Settings

    // #region Change Company
    handleShowCompanyChange = async () => {
        const userCompanies = await AppUtils.callService(() => userService.getUserCompanies());
        if (!userCompanies || userCompanies.companies.length < 2) return null;
        this.setState({
            showCompanies: true,
            companies: userCompanies.companies
        });
        this.handleHideMenu();
    };

    handleChangeCompanyDialogOk = async (companyId: string) => {
        const result = await AppUtils.callService(() => tokenService.changeCompany(companyId));
        if (!result) return;
        this.setState({
            showCompanies: false
        });
        location.reload();
    };

    handleChangeCompanyDialogClose = () => {
        this.setState({
            showCompanies: false
        });
    };
    // #endregion Change Company
    
    render() {
        const props = this.props;
        const state = this.state;
        return (
            <div className="appHeader dark">
                <div className="avatarContainer">
                    <AvatarImage
                        border={true}
                        id={props.user.userCode}
                        name={props.user && props.user.userName ? props.user.userName : "..."}
                        image={props.user && props.user.userPicture ? "data:image/png;base64," + props.user.userPicture : null}
                        onClick={this.handleMenuClick}
                    />
                </div>
                <div className="title">
                    <span className={props.user.canUseMultipleCompanies ? "dropdown-toggle" : ""} onClick={props.user.canUseMultipleCompanies ? this.handleShowCompanyChange : null}>
                        <span style={{ marginRight: "5px" }}>{props.user.companyName}</span>
                    </span>
                </div>
                {state.showMenu &&
                    <HeaderMenu
                        canEditOwnRecord={props.user.canEditOwnRecord}
                        canUseCalendarTasks={props.user.canUseCalendarTasks}
                        onShowUserProfile={this.handleShowUserProfile}
                        onShowSettings={this.handleShowSettings}
                        onLogout={props.onLogout}
                        onHideMenu={this.handleHideMenu}
                    />
                }
                {state.showUserProfile && !!state.userProfileEdit &&
                    <UserProfileDialog
                        userState={props.user}
                        userProfileEdit={state.userProfileEdit}
                        onOk={this.handleUserProfileDialogClose}
                        onCancel={this.handleUserProfileDialogClose}
                    />
                }
                {state.showSettings && !!state.settingsEdit &&
                    <SettingsDialog
                        userState={props.user}
                        settingsEdit={state.settingsEdit}
                        onOk={this.handleSettingsDialogClose}
                        onCancel={this.handleSettingsDialogClose}
                    />
                }
                {state.showCompanies &&
                    <HeaderCompanyMenu
                        companyId={props.user.companyId}
                        companies={state.companies}
                        onChange={this.handleChangeCompanyDialogOk}
                        onHideMenu={this.handleChangeCompanyDialogClose}
                    />
                }
            </div>
        );
    }
}

//SubPageHeader
export interface ISubPageHeaderProp {
    user: IUserState;
    title: string;
    searchIsSet?: boolean;
    onSearch?: () => void;
    onEdit?: () => void;
    onAdd?: () => void;
    onAddFile?: (fileList: FileList) => void;
    onAddPhoto?: () => void;
}

export class SubPageHeader extends React.Component<ISubPageHeaderProp, {}> {
    handleHomeClick = () => {
        customHistory.push("/");
    };

    render() {
        const props = this.props;
        return (
            <div className="appHeader dark">
                <ToolButton
                    classes="home mui white round mono"
                    onClick={this.handleHomeClick}
                />
                <div className="title">
                    <div className="subTitle">{(props.user.companyName ?? " ")}</div>
                    <div>{props.title}</div>
                </div>
                <div className="buttons">
                    {!!props.onAddFile &&
                        <ToolAddFileButton
                            fileInputName="addFile"
                            classes="add white round mono"
                            onChange={props.onAddFile}
                        />
                    }
                    {!!props.onAddPhoto &&
                        <ToolButton
                            classes="camera white round mono"
                            onClick={props.onAddPhoto}
                        />
                    }
                    {!!props.onAdd &&
                        <ToolButton
                            classes="add white round mono"
                            onClick={props.onAdd}
                        />
                    }
                    {!!props.onEdit &&
                        <ToolButton
                            classes="edit round mono smaller"
                            onClick={props.onEdit}
                        />
                    }
                    {!!props.onSearch &&
                        <ToolButton
                            classes={"search" + (props.searchIsSet ? " active" : "") + " white round mono smaller"}
                            onClick={props.onSearch}
                        />
                    }
                </div>
            </div>
        );
    }
}

//EditPageHeader
export interface IEditPageHeaderProp {
    title: string;
    onBackClick: () => void;
    appendElement?: JSX.Element;
}

export class EditPageHeader extends React.Component<IEditPageHeaderProp, { }> {
    handleBackClick = () => {
        this.props.onBackClick();
    };

    render() {
        const props = this.props;
        return (
            <div className="appHeader dark">
                <ToolButton
                    classes="return white round mono"
                    onClick={this.handleBackClick}
                />
                <div className="title">{this.props.title}</div>
                {props.appendElement}
            </div>
        );
    }
}